/**
 * SearchBar - Controlled search input with a leading search icon and a
 * conditional clear button. Used in list views (work orders, assets, etc.)
 * to filter displayed data.
 *
 * This is a controlled component — the parent owns the search value via
 * `value` and `onChange` props. The clear button resets the value to an
 * empty string through the same onChange callback.
 *
 * Enforces min-h-touch (44px) for mobile accessibility.
 *
 * @example
 * const [query, setQuery] = useState('');
 * <SearchBar value={query} onChange={setQuery} placeholder="Search work orders..." />
 */
import React, { useCallback } from 'react';

/**
 * Props for the SearchBar component.
 */
export interface SearchBarProps {
	/** Current search string (controlled) */
	value: string;
	/** Called with the new value on every keystroke or when cleared */
	onChange: (value: string) => void;
	/** Placeholder text shown when the input is empty — defaults to 'Search...' */
	placeholder?: string;
	/** Additional CSS classes to merge onto the wrapper */
	className?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({ value, onChange, placeholder = 'Search...', className = '' }) => {
	// --- Event handlers ---
	// Memoized clear handler to avoid unnecessary re-renders in child components.
	const handleClear = useCallback(() => {
		onChange('');
	}, [onChange]);

	return (
		// max-w-md constrains the search bar width on large screens to keep it readable.
		// 'relative' enables absolute positioning of the icon and clear button inside.
		<div className={['search-bar', 'relative w-full max-w-md', className].join(' ')} data-ref="molecule-search-bar">
			{/* Search icon — decorative only, positioned inside the input's left padding.
				pointer-events-none ensures clicks pass through to the input beneath. */}
			<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-12px">
				<svg className="h-20px w-20px text-brand-text-muted" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
					<path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
				</svg>
			</div>

			{/* The search input itself.
				pl-40px makes room for the search icon; pr-40px makes room for the clear button.
				min-h-touch enforces 44px touch target.
				aria-label uses the placeholder as a fallback since there is no visible <label>. */}
			<input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="search-bar__input block w-full rounded-lg border border-brand-border py-8px pl-40px pr-40px text-16px font-sans min-h-touch placeholder-brand-text-muted focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary" aria-label={placeholder} />

			{/* Clear button — only rendered when the input has a value.
				Positioned absolutely on the right side of the input. */}
			{value && (
				<button type="button" onClick={handleClear} className="absolute inset-y-0 right-0 flex items-center pr-12px text-brand-text-muted hover:text-brand-accent transition-colors" aria-label="Clear search">
					<svg className="h-20px w-20px" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
					</svg>
				</button>
			)}
		</div>
	);
};

export default SearchBar;
